// ─────────────────────────────────────────────────────────────────────────────
//  DomainRenewalSettings — "Domain & Hosting Renewal" panel in System Settings
//
//  Two jobs in one panel:
//
//    1. Always-available copy of the invoice. Staff shouldn't have to wait for
//       the prompt to fire to see what is owed, and an administrator who
//       acknowledged it last week needs somewhere to go back to.
//
//    2. Where the numbers get set, and where a renewal is booked. Pushing
//       `expires_at` forward is what actually stops the prompt — the
//       acknowledgement ledger is keyed to the billing period, so a new expiry
//       date invalidates the old acks and re-arms the whole cycle by itself.
//
//  Editing is restricted to System Administrator. Everyone else who can reach
//  System Settings sees the invoice read-only.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Stack, Button, TextField, Typography, MenuItem, Divider,
  CircularProgress, Alert, Chip, Collapse,
} from '@mui/material';
import {
  Print as PrintIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  EditOutlined as EditIcon,
  AutorenewOutlined as RenewIcon,
} from '@mui/icons-material';
import axios from 'axios';
import Swal from 'sweetalert2';

import { domainRenewalStatus, saveDomainRenewal } from '../BaseURL';
import DomainRenewalInvoice, { printDomainInvoice, formatDate } from './DomainRenewalInvoice';

const CURRENCIES = ['GHS', 'USD', 'GBP', 'EUR', 'NGN'];

const readUser = () => {
  try { return JSON.parse(localStorage.getItem('userInfo') || 'null'); }
  catch { return null; }
};

const emptyForm = {
  domainName: '', registrar: 'Namecheap Inc.', registeredOn: '', expiresAt: '',
  graceUntil: '', renewalAmount: '', currency: 'GHS', renewalYears: 1,
  invoiceNo: '', billingNotes: '', status: 'active',
};

const DomainRenewalSettings = () => {
  const [status, setStatus]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm]       = useState(emptyForm);

  const user       = readUser();
  const canEdit    = user?.role === 'System Administrator';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(domainRenewalStatus);
      setStatus(res.data);

      const inv = res.data?.invoice;
      if (inv) {
        setForm({
          domainName:    inv.domainName    || '',
          registrar:     inv.registrar     || 'Namecheap Inc.',
          registeredOn:  inv.registeredOn  || '',
          expiresAt:     inv.expiresAt     || '',
          graceUntil:    inv.graceUntil    || '',
          renewalAmount: inv.renewalAmount == null ? '' : String(inv.renewalAmount),
          currency:      inv.currency      || 'GHS',
          renewalYears:  inv.renewalYears  || 1,
          invoiceNo:     inv.invoiceNo     || '',
          billingNotes:  inv.billingNotes  || '',
          status:        inv.status        || 'active',
        });
      }
    } catch (err) {
      console.warn('[domain-renewal] load failed:', err.message);
      setStatus({ success: false, configured: false });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setField = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const handlePrint = () => {
    const ok = printDomainInvoice(status);
    if (ok === false) {
      Swal.fire({
        icon: 'info',
        title: 'Pop-up blocked',
        text: 'Allow pop-ups for this site to print or save the invoice as PDF.',
        confirmButtonColor: '#11998e',
      });
    }
  };

  const handleSave = async () => {
    if (!form.expiresAt) {
      await Swal.fire({ icon: 'warning', title: 'Expiry date required', text: 'Set the paid-through date before saving.', confirmButtonColor: '#11998e' });
      return;
    }

    // Moving the expiry forward is a billing event, not a typo-level edit — the
    // prompt stops for every user the moment it saves. Make that explicit.
    const movedForward = status?.invoice?.expiresAt && form.expiresAt > status.invoice.expiresAt;
    if (movedForward) {
      const confirm = await Swal.fire({
        icon: 'question',
        title: 'Book this renewal?',
        html: `
          <div style="text-align:left;font-size:0.9rem">
            <p>Paid-through date moves from <b>${formatDate(status.invoice.expiresAt)}</b>
               to <b>${formatDate(form.expiresAt)}</b>.</p>
            <p style="color:#2e8b57">The renewal prompt will stop appearing for all users,
               and will re-arm automatically for the next period.</p>
          </div>`,
        showCancelButton: true,
        confirmButtonColor: '#2e8b57',
        cancelButtonColor: '#9e9e9e',
        confirmButtonText: 'Yes, book it',
      });
      if (!confirm.isConfirmed) return;
    }

    setSaving(true);
    try {
      const res = await axios.post(saveDomainRenewal, {
        ...form,
        updatedBy: user?.username || user?.name || null,
      });
      if (res.data.success) {
        await Swal.fire({
          icon: 'success',
          title: movedForward ? 'Renewal booked' : 'Saved',
          text: res.data.message,
          timer: 2200,
          showConfirmButton: false,
        });
        setEditing(false);
        await load();
      } else {
        throw new Error(res.data.message || 'Save failed');
      }
    } catch (err) {
      await Swal.fire({
        icon: 'error',
        title: 'Save Failed',
        text: err.response?.data?.message || err.message,
        confirmButtonColor: '#c0392b',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (!status?.configured) {
    return (
      <Alert severity="info" sx={{ fontSize: '0.82rem' }}>
        No domain licence record exists for this facility in the central database yet.
        {status?.message ? ` (${status.message})` : ''} Contact TotalCare to have it created —
        once the row exists, the renewal details can be managed here.
      </Alert>
    );
  }

  const fieldSx = { '& .MuiInputBase-input': { fontSize: '0.84rem' } };

  return (
    <Stack spacing={2}>
      {/* ── Toolbar ──────────────────────────────────────────────────── */}
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Chip
          size="small"
          icon={<RenewIcon sx={{ fontSize: '15px !important' }} />}
          label={`Paid through ${formatDate(status.invoice.expiresAt)}`}
          sx={{ fontWeight: 600, fontSize: '0.72rem' }}
        />
        {status.acknowledged && (
          <Chip
            size="small"
            label={`Reviewed by ${status.acknowledgedBy}`}
            sx={{ fontSize: '0.72rem', background: '#e9f7ef', color: '#2e8b57', fontWeight: 600 }}
          />
        )}

        <Box sx={{ flex: 1 }} />

        <Button size="small" onClick={load} startIcon={<RefreshIcon />}
          sx={{ textTransform: 'none', color: '#475569' }}>
          Refresh
        </Button>
        <Button size="small" onClick={handlePrint} startIcon={<PrintIcon />} variant="outlined"
          sx={{ textTransform: 'none', color: '#475569', borderColor: '#cbd5e1' }}>
          Print / Save PDF
        </Button>
        {canEdit && (
          <Button
            size="small" variant={editing ? 'outlined' : 'contained'}
            onClick={() => setEditing(e => !e)}
            startIcon={<EditIcon />}
            sx={{
              textTransform: 'none', fontWeight: 600,
              ...(editing ? {} : { background: '#11998e', '&:hover': { background: '#0e8a7f' } }),
            }}
          >
            {editing ? 'Cancel edit' : 'Edit / Book renewal'}
          </Button>
        )}
      </Stack>

      {/* ── Editor ───────────────────────────────────────────────────── */}
      <Collapse in={editing} unmountOnExit>
        <Box sx={{ p: 2, border: '1px solid #e2e8f0', borderRadius: 2, background: '#f8fafc' }}>
          <Typography sx={{
            fontSize: '0.68rem', fontWeight: 800, letterSpacing: 1,
            color: '#94a3b8', textTransform: 'uppercase', mb: 1.5,
          }}>
            Renewal details
          </Typography>

          <Stack spacing={1.5}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <TextField
                label="Domain name" size="small" fullWidth sx={fieldSx}
                value={form.domainName}
                onChange={e => setField('domainName', e.target.value)}
                placeholder="facility.yourcareconnect.online"
              />
              <TextField
                label="Registrar" size="small" fullWidth sx={fieldSx}
                value={form.registrar}
                onChange={e => setField('registrar', e.target.value)}
                helperText="Where the domain is actually held"
              />
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <TextField
                label="Registered on" type="date" size="small" fullWidth sx={fieldSx}
                InputLabelProps={{ shrink: true }}
                value={form.registeredOn}
                onChange={e => setField('registeredOn', e.target.value)}
              />
              <TextField
                label="Paid through (expiry) *" type="date" size="small" fullWidth sx={fieldSx}
                InputLabelProps={{ shrink: true }}
                value={form.expiresAt}
                onChange={e => setField('expiresAt', e.target.value)}
                helperText="Move this forward to book a renewal"
              />
              <TextField
                label="Goes offline" type="date" size="small" fullWidth sx={fieldSx}
                InputLabelProps={{ shrink: true }}
                value={form.graceUntil}
                onChange={e => setField('graceUntil', e.target.value)}
                helperText="End of registrar grace period"
              />
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <TextField
                label="Renewal amount" type="number" size="small" fullWidth sx={fieldSx}
                value={form.renewalAmount}
                onChange={e => {
                  const v = e.target.value;
                  if (v === '' || parseFloat(v) >= 0) setField('renewalAmount', v);
                }}
                inputProps={{ min: 0, step: '0.01' }}
              />
              <TextField
                label="Currency" select size="small" fullWidth sx={fieldSx}
                value={form.currency}
                onChange={e => setField('currency', e.target.value)}
              >
                {CURRENCIES.map(c => (
                  <MenuItem key={c} value={c}>
                    <Typography sx={{ fontSize: '0.84rem' }}>{c}</Typography>
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Period (years)" type="number" size="small" fullWidth sx={fieldSx}
                value={form.renewalYears}
                onChange={e => setField('renewalYears', e.target.value)}
                inputProps={{ min: 1, max: 10, step: 1 }}
              />
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <TextField
                label="Invoice number" size="small" fullWidth sx={fieldSx}
                value={form.invoiceNo}
                onChange={e => setField('invoiceNo', e.target.value)}
                helperText="Shown on the printed invoice"
              />
              <TextField
                label="Licence status" select size="small" fullWidth sx={fieldSx}
                value={form.status}
                onChange={e => setField('status', e.target.value)}
              >
                {['active', 'suspended'].map(s => (
                  <MenuItem key={s} value={s}>
                    <Typography sx={{ fontSize: '0.84rem' }}>{s}</Typography>
                  </MenuItem>
                ))}
              </TextField>
            </Stack>

            <TextField
              label="Payment instructions" size="small" fullWidth multiline minRows={2} sx={fieldSx}
              value={form.billingNotes}
              onChange={e => setField('billingNotes', e.target.value)}
              placeholder="Mobile money number, bank details, or who to contact to settle this invoice."
              helperText="Printed on the invoice under the line items"
            />

            <Divider />

            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button onClick={() => { setEditing(false); load(); }} disabled={saving}
                sx={{ textTransform: 'none', color: '#64748b' }}>
                Cancel
              </Button>
              <Button
                variant="contained" onClick={handleSave} disabled={saving}
                startIcon={saving ? <CircularProgress size={15} color="inherit" /> : <SaveIcon />}
                sx={{
                  textTransform: 'none', fontWeight: 700,
                  background: '#11998e', '&:hover': { background: '#0e8a7f' },
                }}
              >
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Collapse>

      {/* ── The invoice ──────────────────────────────────────────────── */}
      <DomainRenewalInvoice data={status} />

      {!canEdit && (
        <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8', textAlign: 'center' }}>
          Only a System Administrator can change renewal details.
        </Typography>
      )}
    </Stack>
  );
};

export default DomainRenewalSettings;
